import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, MailCheck, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AuthShell } from "@/pages/AuthPage";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";

export function VerifyEmail() {
  const { L } = useApp();
  const [params] = useSearchParams();
  const { user, refresh } = useAuth();
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    api.post("/auth/verify-email", { token: params.get("token") || "" })
      .then(() => { setState("ok"); refresh(); })
      .catch((e) => { setError(formatApiError(e)); setState("error"); });
  }, [params, refresh]);
  return (
    <AuthShell>
      <div className="space-y-5" data-testid="verify-email-page" data-state={state}>
        {state === "loading" && <Loader2 className="animate-spin text-violet-600" />}
        {state === "ok" && (
          <>
            <CheckCircle2 size={36} className="text-emerald-500" />
            <h1 className="text-3xl font-extrabold tracking-tight">{L("Emel disahkan", "Email verified")}</h1>
            <p className="text-sm text-slate-500">{L("Akaun anda kini aktif.", "Your account is now active.")}</p>
            <Button asChild className="h-11 w-full rounded-xl bg-violet-600 hover:bg-violet-700" data-testid="verify-continue-btn">
              <Link to={sessionStorage.getItem("minitai-join") ? `/join/${sessionStorage.getItem("minitai-join")}` : user ? (user.onboarded ? "/app" : "/onboarding") : "/login"}>{L("Teruskan", "Continue")}</Link>
            </Button>
          </>
        )}
        {state === "error" && (
          <>
            <AlertTriangle size={36} className="text-red-500" />
            <h1 className="text-3xl font-extrabold tracking-tight">{L("Pengesahan gagal", "Verification failed")}</h1>
            <p className="text-sm text-red-700" data-testid="verify-error">{error}</p>
            <Link to={user ? "/app" : "/login"} className="block text-sm font-semibold text-violet-700">{L("Minta pautan baharu selepas log masuk", "Request a new link after logging in")}</Link>
          </>
        )}
      </div>
    </AuthShell>
  );
}

export function VerifyPending() {
  const { L } = useApp();
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [devLink, setDevLink] = useState("");
  const resend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/resend-verification");
      if (data.already_verified) return refresh();
      setDevLink(data.dev_verify_link || "");
      if (data.email_sent) toast.success(L("Emel pengesahan dihantar semula.", "Verification email resent."));
      else toast.warning(L("Emel tidak dapat dihantar sekarang. Cuba lagi dalam beberapa minit.", "Email could not be sent right now. Try again in a few minutes."));
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <AuthShell>
      <div className="space-y-5" data-testid="verify-pending-page">
        <div className="grid h-12 w-12 place-items-center rounded-2xl bg-violet-50 text-violet-600"><MailCheck size={22} /></div>
        <h1 className="text-3xl font-extrabold tracking-tight">{L("Sahkan emel anda", "Verify your email")}</h1>
        <p className="text-sm text-slate-500">
          {L("Kami telah menghantar pautan pengesahan ke ", "We sent a verification link to ")}<strong className="text-slate-800" data-testid="verify-pending-email">{user.email}</strong>.
          {L(" Klik pautan itu untuk mula menggunakan MinitAI. Semak folder spam jika tiada.", " Click it to start using MinitAI. Check your spam folder if you can't find it.")}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button onClick={resend} disabled={busy} className="h-11 rounded-xl bg-violet-600 hover:bg-violet-700" data-testid="resend-verification-btn">{L("Hantar semula emel", "Resend email")}</Button>
          <Button onClick={refresh} variant="outline" className="h-11 rounded-xl" data-testid="verify-check-btn">{L("Saya sudah sahkan", "I've verified")}</Button>
        </div>
        {devLink && <a href={devLink} className="block break-all text-xs font-semibold text-violet-700" data-testid="dev-verify-link">{L("[Mod pembangunan] Buka pautan pengesahan", "[Dev mode] Open verification link")}</a>}
        <button onClick={async () => { await logout(); navigate("/login"); }} className="text-sm font-semibold text-slate-500 hover:text-slate-800" data-testid="verify-logout-btn">{L("Log keluar", "Log out")}</button>
      </div>
    </AuthShell>
  );
}
